<template>
  <ReportShell
    v-model="dialog"
    title="Watchlist Screening Report"
    subtitle="Compliance Screening Run Summary"
    :header-name="triggerLabel"
    :ref-label="'Session ' + shortRef(report.sessionId)"
    @print="printReport"
  >
    <ReportStatGrid :stats="headlineStats" />

    <ReportSection title="Report Information">
      <table class="doc-table">
        <tbody>
          <tr><td class="doc-label">Session reference</td><td style="font-family:monospace;font-size:12px">{{ report.sessionId }}</td></tr>
          <tr><td class="doc-label">Search type</td><td>{{ triggerLabel }}</td></tr>
          <tr><td class="doc-label">Initiated by</td><td>{{ officerName }}</td></tr>
          <tr><td class="doc-label">Search date</td><td>{{ formatDateTime(report.dateCreated) }}</td></tr>
          <tr><td class="doc-label">Completed</td><td>{{ formatDateTime(report.dateCompleted) }}</td></tr>
          <tr><td class="doc-label">Regulatory basis</td><td>AML/CFT compliance screening — FATF Recommendation 10 / Proceeds of Crime Act / Terrorism Prevention Act</td></tr>
        </tbody>
      </table>
      <div class="doc-note">
        A total of <strong>{{ report.totalAccounts }}</strong> account holder{{ report.totalAccounts !== 1 ? 's were' : ' was' }}
        screened against international and domestic watchlists on <strong>{{ formatDate(report.dateCreated) }}</strong>.
        <template v-if="report.possibleMatches > 0">
          <strong>{{ report.possibleMatches }}</strong> possible match{{ report.possibleMatches !== 1 ? 'es were' : ' was' }}
          identified and {{ report.possibleMatches !== 1 ? 'have' : 'has' }} been flagged for officer review.
        </template>
        <template v-else>
          No possible matches were identified. All screened accounts returned clear results across all providers searched.
        </template>
      </div>
    </ReportSection>

    <ReportSection
      title="Accounts Requiring Review"
      v-if="hitAccounts.length"
      :badge="hitAccounts.length"
      badge-tone="danger"
    >
      <p class="doc-muted" style="font-size:12px;margin:0 0 12px">
        The following accounts returned one or more possible matches and require review by a compliance officer.
      </p>
      <div v-for="(acc, ai) in hitAccounts" :key="acc.accountId" class="doc-subblock">
        <div class="doc-subblock-header">
          <span class="doc-subblock-title">{{ ai + 1 }}. {{ toTitleCase(acc.personName) }} &mdash; {{ acc.mainAccountNumber }}</span>
          <span class="doc-pill" style="background:#FCEBEB;color:#791F1F">Possible match</span>
        </div>
        <div class="doc-subblock-body">
          <table class="doc-table doc-table--tight">
            <thead><tr><th>Provider</th><th>Result</th><th>Score</th><th>Matched name</th><th>Flag status</th></tr></thead>
            <tbody>
              <tr v-for="p in acc.providers" :key="p.provider">
                <td>{{ providerShort(p.provider) }}</td>
                <td><span class="doc-pill" :style="watchlistPillStyle(p.matchStatus)">{{ p.matchStatus === 'POSSIBLE_MATCH' ? 'Possible match' : 'No match' }}</span></td>
                <td class="doc-num">{{ p.matchScore || '—' }}</td>
                <td class="doc-muted">{{ p.matchDetails && p.matchDetails[0] ? toTitleCase(p.matchDetails[0].name) : '—' }}</td>
                <td class="doc-muted">{{ p.resultingFlag ? flagStatusLabel(p.resultingFlag.flagStatus) : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </ReportSection>

    <ReportSection title="Accounts Returning Clear Results" :badge="clearAccounts.length" badge-tone="success">
      <p class="doc-muted" style="font-size:12px;margin:0 0 12px">
        No further action is required for these accounts.
        <span v-if="clearAccounts.length > clearPreviewLimit">
          Showing the first {{ clearPreviewLimit }} of {{ clearAccounts.length }} — the full list is available in the account screening log.
        </span>
      </p>
      <table v-if="clearAccounts.length" class="doc-table">
        <thead><tr><th>Account holder</th><th>Account number</th><th>Providers searched</th></tr></thead>
        <tbody>
          <tr v-for="acc in clearAccountsPreview" :key="acc.accountId">
            <td>{{ toTitleCase(acc.personName) }}</td>
            <td class="doc-muted">{{ acc.mainAccountNumber }}</td>
            <td class="doc-muted">{{ acc.providers.map(p => providerShort(p.provider)).join(', ') }}</td>
          </tr>
        </tbody>
      </table>
      <div v-else class="doc-empty">No accounts returned clear results in this run.</div>
    </ReportSection>

    <ReportSection title="Certification">
      <div class="doc-signoff">
        <p>
          This report was generated automatically by the compliance system on <strong>{{ formatDateTime(new Date()) }}</strong>
          and reflects the results of watchlist screening conducted on <strong>{{ formatDate(report.dateCreated) }}</strong>.
        </p>
        <p>{{ certificationListsText }}</p>
        <p>
          Results marked as "Possible match" require review by an authorised compliance officer. This report does not
          constitute a final determination of any individual's status on any watchlist and should be used solely as an
          input to the compliance review process.
        </p>
        <div class="doc-sign-row">
          <div class="doc-sign-block"><div class="doc-sign-line"></div><div class="doc-sign-label">Reviewing Officer</div></div>
          <div class="doc-sign-block"><div class="doc-sign-line"></div><div class="doc-sign-label">Date reviewed</div></div>
          <div class="doc-sign-block"><div class="doc-sign-line"></div><div class="doc-sign-label">CMLCO / Designated Officer</div></div>
        </div>
      </div>
    </ReportSection>

  </ReportShell>
</template>

<script>
import ReportShell from '../../../../shared/reports-kit/components/ReportShell.vue';
import ReportSection from '../../../../shared/reports-kit/components/ReportSection.vue';
import ReportStatGrid from '../../../../shared/reports-kit/components/ReportStatGrid.vue';
import reportFormatMixin from '../../../../shared/reports-kit/mixins/reportFormat.mixin';
import reportPrintMixin from '../../../../shared/reports-kit/mixins/reportPrint.mixin';
import { providerLabel, providerShort, PROVIDER_CODES } from '@/modules/amlpro/services/watchlist-sources';

const CLEAR_PREVIEW_LIMIT = 50;

const FLAG_STATUS_LABELS = {
  POSSIBLE_MATCH_UNREVIEWED: 'Unreviewed',
  UNDER_REVIEW:              'Under review',
  TRUE_POSITIVE:             'Confirmed hit',
  FALSE_POSITIVE:            'Cleared',
};

export default {
  name: 'WatchlistReportPrint',

  components: { ReportShell, ReportSection, ReportStatGrid },

  mixins: [reportFormatMixin, reportPrintMixin],

  props: {
    value:  { type: Boolean, default: false },
    report: { type: Object,  required: true },
  },

  data: () => ({
    dialog: false,
    clearPreviewLimit: CLEAR_PREVIEW_LIMIT,
  }),

  watch: {
    value(val)  { this.dialog = val; },
    dialog(val) { this.$emit('input', val); },
  },

  computed: {
    hitAccounts() {
      return (this.report.accounts ?? [])
        .filter(a => a.providers.some(p => p.matchStatus === 'POSSIBLE_MATCH'))
        .sort((a, b) => {
          const aScore = Math.max(...a.providers.map(p => p.matchScore ?? 0));
          const bScore = Math.max(...b.providers.map(p => p.matchScore ?? 0));
          return bScore - aScore;
        });
    },
    clearAccounts() {
      return (this.report.accounts ?? []).filter(a => a.providers.every(p => p.matchStatus !== 'POSSIBLE_MATCH'));
    },
    clearAccountsPreview() {
      return this.clearAccounts.slice(0, this.clearPreviewLimit);
    },
    suppressed() {
      if (!this.report.accounts) return 0;
      return this.report.accounts.reduce((s, a) => s + a.providers.filter(p => p.matchStatus === 'SUPPRESSED').length, 0);
    },
    errors() {
      if (!this.report.accounts) return 0;
      return this.report.accounts.reduce((s, a) => s + a.providers.filter(p => p.matchStatus === 'ERROR').length, 0);
    },
    officerName() {
      const u = this.report.initiatedBy;
      if (!u) return 'System (automated)';
      const name = u.person ? `${u.person.firstName ?? ''} ${u.person.lastName ?? ''}`.trim() : '';
      return this.toTitleCase(name || u.username || 'Unknown officer');
    },

    certificationListsText() {
      const codes = new Set();
      for (const a of (this.report.accounts ?? [])) {
        for (const p of (a.providers ?? [])) if (p.provider) codes.add(p.provider);
      }
      const names = [...(codes.size ? codes : PROVIDER_CODES)].map(c => providerLabel(c));
      return `The screening was performed against the following sanctions, criminal and politically exposed persons lists: ${names.join('; ')}.`;
    },
    triggerLabel() {
      const map = { MANUAL: 'Manual search', ONBOARDING: 'Account onboarding', PERIODIC: 'Periodic screening' };
      return map[this.report.triggerType] ?? this.report.triggerType ?? 'Manual search';
    },
    headlineStats() {
      return [
        { label: 'Accounts screened', value: this.report.totalAccounts, tone: 'neutral' },
        { label: 'Searches performed', value: this.report.totalSearches, tone: 'neutral' },
        { label: 'Possible matches', value: this.report.possibleMatches, tone: this.report.possibleMatches ? 'danger' : 'success' },
        { label: 'No matches', value: this.report.noMatches, tone: 'success' },
        { label: 'Suppressed', value: this.suppressed, tone: 'neutral' },
        { label: 'Errors', value: this.errors, tone: this.errors ? 'warning' : 'neutral' },
      ];
    },
  },

  methods: {
    providerShort,

    flagStatusLabel(s) {
      return FLAG_STATUS_LABELS[s] ?? s;
    },

    printReport() {
      const e = this.escapeHtml.bind(this);
      const r = this.report;

      const hitAccountsHtml = this.hitAccounts.map((acc, ai) => this.buildSubblockHtml({
        title: `${ai + 1}. ${this.toTitleCase(acc.personName)} — ${acc.mainAccountNumber}`,
        badgeHtml: `<span class="pill" style="background:#FCEBEB;color:#791F1F">Possible match</span>`,
        bodyHtml: `
          <table class="tight">
            <thead><tr><th>Provider</th><th>Result</th><th>Score</th><th>Matched name</th><th>Flag status</th></tr></thead>
            <tbody>
              ${acc.providers.map(p => `
                <tr>
                  <td>${e(this.providerShort(p.provider))}</td>
                  <td><span class="pill" style="${this.watchlistPillStyle(p.matchStatus)}">${p.matchStatus === 'POSSIBLE_MATCH' ? 'Possible match' : 'No match'}</span></td>
                  <td class="num">${e(p.matchScore || '—')}</td>
                  <td class="dim">${p.matchDetails && p.matchDetails[0] ? e(this.toTitleCase(p.matchDetails[0].name)) : '—'}</td>
                  <td class="dim">${p.resultingFlag ? e(this.flagStatusLabel(p.resultingFlag.flagStatus)) : '—'}</td>
                </tr>`).join('')}
            </tbody>
          </table>`,
      })).join('');

      const clearRowsHtml = this.clearAccountsPreview.map(acc => `
        <tr>
          <td>${e(this.toTitleCase(acc.personName))}</td>
          <td class="dim">${e(acc.mainAccountNumber)}</td>
          <td class="dim">${e(acc.providers.map(p => this.providerShort(p.provider)).join(', '))}</td>
        </tr>`).join('');

      const clearNote = this.clearAccounts.length > this.clearPreviewLimit
        ? `Showing the first ${this.clearPreviewLimit} of ${this.clearAccounts.length} — the full list is available in the account screening log.`
        : '';

      const bodyHtml = `
        ${this.buildPrintLetterheadHtml({
          title: 'Watchlist Screening Report',
          subtitle: 'Compliance Screening Run Summary',
          refLabel: 'Session ' + this.shortRef(r.sessionId),
        })}

        ${this.buildStatGridHtml(this.headlineStats)}

        ${this.buildSectionHtml({
          number: 'I',
          title: 'Report Information',
          bodyHtml: `
            <table><tbody>
              <tr><td class="label">Session reference</td><td style="font-family:monospace">${e(r.sessionId)}</td></tr>
              <tr><td class="label">Search type</td><td>${e(this.triggerLabel)}</td></tr>
              <tr><td class="label">Initiated by</td><td>${e(this.officerName)}</td></tr>
              <tr><td class="label">Search date</td><td>${e(this.formatDateTime(r.dateCreated))}</td></tr>
              <tr><td class="label">Completed</td><td>${e(this.formatDateTime(r.dateCompleted))}</td></tr>
              <tr><td class="label">Regulatory basis</td><td>AML/CFT compliance screening &mdash; FATF Recommendation 10 / Proceeds of Crime Act / Terrorism Prevention Act</td></tr>
            </tbody></table>
            <div class="note">
              A total of ${e(r.totalAccounts)} account holder${r.totalAccounts !== 1 ? 's were' : ' was'} screened on ${e(this.formatDate(r.dateCreated))}.
              ${r.possibleMatches > 0
                ? `${e(r.possibleMatches)} possible match${r.possibleMatches !== 1 ? 'es were' : ' was'} identified and flagged for officer review.`
                : `No possible matches were identified.`}
            </div>`,
        })}

        ${this.hitAccounts.length ? this.buildSectionHtml({
          number: 'II',
          title: 'Accounts Requiring Review',
          badgeHtml: `<span class="pill badge" style="background:#FCEBEB;color:#791F1F">${this.hitAccounts.length}</span>`,
          bodyHtml: hitAccountsHtml,
        }) : ''}

        ${this.buildSectionHtml({
          number: this.hitAccounts.length ? 'III' : 'II',
          title: 'Accounts Returning Clear Results',
          badgeHtml: `<span class="pill badge" style="background:#EAF3DE;color:#27500A">${this.clearAccounts.length}</span>`,
          bodyHtml: this.clearAccounts.length ? `
            ${clearNote ? `<div class="empty" style="margin-bottom:8px">${e(clearNote)}</div>` : ''}
            <table>
              <thead><tr><th>Account holder</th><th>Account number</th><th>Providers searched</th></tr></thead>
              <tbody>${clearRowsHtml}</tbody>
            </table>` : `<div class="empty">No accounts returned clear results in this run.</div>`,
        })}

        ${this.buildSectionHtml({
          number: this.hitAccounts.length ? 'IV' : 'III',
          title: 'Certification',
          bodyHtml: `
            <div class="signoff">
              <p>This report was generated automatically by the compliance system on ${e(this.formatDateTime(new Date()))} and reflects the results of watchlist screening conducted on ${e(this.formatDate(r.dateCreated))}.</p>
              <p>${e(this.certificationListsText)}</p>
              <p>Results marked as "Possible match" require review by an authorised compliance officer. This report does not constitute a final determination of any individual's status on any watchlist.</p>
              <div class="sign-row">
                <div class="sign-block"><div class="sign-line"></div><div class="sign-label">Reviewing Officer</div></div>
                <div class="sign-block"><div class="sign-line"></div><div class="sign-label">Date reviewed</div></div>
                <div class="sign-block"><div class="sign-line"></div><div class="sign-label">CMLCO / Designated Officer</div></div>
              </div>
            </div>`,
        })}

        ${this.buildPrintFooterHtml()}
      `;

      this.printHtml({ title: `Watchlist Screening Run - ${r.sessionId}`, bodyHtml });
    },
  },
};
</script>